import * as React from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, DropdownOption } from '@ixicare/ui';
import { ManualStatus, OccupiedDurationSeconds, ProfileType } from '@ixicare/resources';
import { LogOut, User } from 'lucide-react';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import ImpersonationBanner from '~components/ImpersonationBanner/ImpersonationBanner';
import { StatusPicker } from '~components/Presence';
import HcpGroupSelector from './HcpGroupSelector';
import MutedIndicator from './MutedIndicator';
import SiteSelector from './SiteSelector';
import { useLanguageDropdownEntry } from './useLanguageDropdownEntry';
import styles from './TopBar.module.css';

const TopBar: React.FC = observer(() => {
  const navigate = useNavigate();
  const { auth, permissions, presence, sites } = useStores();
  const { entry: languageEntry, modal: languageModal } = useLanguageDropdownEntry();

  // Sites are loaded for every authenticated user (not just users with can_view_site),
  // so single-site HCPs also have sites.selectedSite populated. Site-scoped pages
  // (hcp-groups, configuration, missing devices, …) gate their queries on it.
  const userId = auth.user?.id;
  const userSiteUuid = auth.user?.site?.uuid;
  React.useEffect(() => {
    if (userId === undefined) return;
    sites.loadSites(userSiteUuid, userId);
  }, [sites, userId, userSiteUuid]);

  if (!auth.user) return null;

  const isHcp = auth.userType === ProfileType.HealthcareProvider;
  const showNotificationIndicator = isHcp || auth.userType === ProfileType.WatcherrEmployee;
  const showStatusPicker = !auth.isImpersonating;

  const dropDownEntries: DropdownOption[] = auth.isImpersonating
    ? [
        {
          label: t('user.actions.exitLoginAs'),
          lucide: LogOut,
          onClick: async () => {
            await auth.exitLoginAs();
            navigate('/healthcare-providers');
          },
        },
        languageEntry,
      ]
    : [
        {
          label: t('user.actions.view'),
          lucide: User,
          onClick: () => auth.user && navigate(`/healthcare-providers/${auth.user.id}/edit`),
        },
        languageEntry,
        {
          label: t('user.actions.logout'),
          lucide: LogOut,
          onClick: async () => {
            await auth.logout();
            navigate('/login');
          },
        },
      ];

  return (
    <div className={styles.TopBar}>
      <div className={styles.LeftSlot}>
        {isHcp && <HcpGroupSelector />}
        {permissions.has('can_view_site') && <SiteSelector />}
      </div>

      <div className={styles.RightSlot}>
        <ImpersonationBanner />
        {showStatusPicker && (
          <StatusPicker
            currentStatus={presence.ownPresence}
            selectedDuration={presence.lastSelectedOccupiedDuration}
            onSelectOccupied={(seconds: OccupiedDurationSeconds) =>
              void presence.setStatus(ManualStatus.Occupied, seconds)
            }
            onSelectUnavailable={() => void presence.setStatus(ManualStatus.Unavailable, null)}
            onClear={() => void presence.clearStatus()}
          />
        )}
        {showNotificationIndicator && <MutedIndicator />}
        <Dropdown name={`${auth.user.firstName} ${auth.user.lastName}`} options={dropDownEntries} />
      </div>
      {languageModal}
    </div>
  );
});

export default TopBar;
